/**
 * Multi-Tenant Configuration
 *
 * Erkennt anhand der Domain, welcher Mandant aktiv ist,
 * und liefert die passende Konfiguration (Supabase, Branding, E-Mail).
 */

export interface TenantBrandingConfig {
  name: string
  logo_url: string
  primary_color: string
  favicon_url?: string
}

export interface TenantConfig {
  slug: string
  branding: TenantBrandingConfig
  supabase: {
    url: string
    anon_key: string
  }
  email_from: string
  email_from_name: string
  consent_text?: string
}

/**
 * Mandanten-Registry.
 *
 * Für das MVP wird hier statisch konfiguriert.
 * Neue Mandanten: einfach einen Eintrag hinzufügen und Domain in DNS + Hosting zeigen lassen.
 */
const tenants: Record<string, TenantConfig> = {
  // Standalone / Default — bestehendes Setup
  'leads.standwert.info': {
    slug: 'standwert',
    branding: {
      name: 'Lead Magnifier',
      logo_url: '/assets/standwert-logo.svg',
      primary_color: '#1B5E6B',
    },
    supabase: {
      url: 'https://kqjqkzqurjeemztcvwev.supabase.co',
      anon_key:
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtxanFrenF1cmplZW16dGN2d2V2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYyNjEyMjcsImV4cCI6MjA5MTgzNzIyN30.etp_BD94YiTkIQe3o9GIXhCd8FxQhJneGk893lNMVl8',
    },
    email_from: 'leads@standwert.info',
    email_from_name: 'STANDWERT Lead Magnifier',
  },

  // Demo-Mandant — für Kundenpräsentation
  // Supabase-Credentials werden nach Anlegen des zweiten Projekts eingetragen
  'leads.messe-innsbruck.at': {
    slug: 'messe-innsbruck',
    branding: {
      name: 'Messe Innsbruck Lead Service',
      logo_url: '/assets/mzi-logo.svg',
      primary_color: '#C41E3A',
    },
    supabase: {
      url: 'https://DEIN-ZWEITES-PROJEKT.supabase.co',
      anon_key: 'DEIN-ANON-KEY',
    },
    email_from: 'leads@messe-innsbruck.at',
    email_from_name: 'Messezentrum Innsbruck',
    consent_text:
      'Ich stimme zu, dass meine Daten vom Messezentrum Innsbruck zur Kontaktaufnahme verwendet werden.',
  },
}

/**
 * Mapping für lokale Entwicklung.
 * localhost / 127.0.0.1 → Standalone-Mandant
 * mzi.localhost → Demo-Mandant (benötigt Eintrag in /etc/hosts: 127.0.0.1 mzi.localhost)
 */
const devHostMapping: Record<string, string> = {
  'localhost': 'leads.standwert.info',
  '127.0.0.1': 'leads.standwert.info',
  'mzi.localhost': 'leads.messe-innsbruck.at',
  // Cloudflare Pages Domains
  'lead-magnifier-app.pages.dev': 'leads.standwert.info',
}

// Fallback-Domain
const DEFAULT_TENANT_DOMAIN = 'leads.standwert.info'

/**
 * Ermittelt die aktive Mandanten-Config anhand des Hostnamens.
 */
export function getTenantConfig(): TenantConfig {
  const hostname = window.location.hostname

  // Zuerst Dev-Mapping prüfen
  const mappedDomain = devHostMapping[hostname]
  if (mappedDomain && tenants[mappedDomain]) {
    return tenants[mappedDomain]
  }

  // Dann direktes Domain-Matching
  if (tenants[hostname]) {
    return tenants[hostname]
  }

  // Fallback
  return tenants[DEFAULT_TENANT_DOMAIN]
}

/**
 * Gibt den aktuellen Tenant-Slug zurück (z.B. für Logging).
 */
export function getTenantSlug(): string {
  return getTenantConfig().slug
}
